import {useParams} from 'react-router-dom'
import {appsAPI} from '../../../../services/AppsService'
import {KTSVG} from '../../../../template/helpers'
import {Domain} from './Domain'
import * as Yup from 'yup'
import {IgnoreLines} from './IgnoreLines'
import {UseB24Users} from '../crm/bitrix24/UseB24Users'
import {RecordURL} from '../pbx/asterisk/RecordURL'
import {useState} from 'react'
import {ITrunkModel} from '../../../../types/apps/common/TrunkModel'
import {useFormik} from 'formik'
import {IOtherSettingsCreate, OtherSettingsInitValues} from '../../core/_models'
import {IAppsModel} from '../../../../types/apps/AppsModel'
import {AccessToken1C} from '../crm/onec/AccessToken1C'
import {useIntl} from 'react-intl'
import {RoistatSettings} from '../crm/roistat/Roistat'
import clsx from 'clsx'
import {Bitrix24ModeSwitchModal} from '../modals/Bitrix24ModeSwitchModal'
import {Bitrix24AppCheck} from '../crm/bitrix24/Bitrix24AppCheck'

const OtherSettingsEditShema = Yup.object().shape({
  crm_domain: Yup.string().required('Domain is required'),
  id_project: Yup.string(),
  api_key: Yup.string(),
  record_url: Yup.string(),
  ignore_trunks: Yup.string(),
  users_in_service: Yup.boolean(),
  mode_is_cloud: Yup.string(),
  bitrix24_client_id: Yup.string().when('mode_is_cloud', {
    is: (val: string) => val === 'LOCAL',
    then: (schema) => schema.required('Обязательное поле'),
  }),
  bitrix24_client_secret: Yup.string().when('mode_is_cloud', {
    is: (val: string) => val === 'LOCAL',
    then: (schema) => schema.required('Обязательное поле'),
  }),
})

const check_ignore_trunks = (ignore_trunks: string) => {
  let ignore_trunks_edit = ignore_trunks.replace(/[.:;*-/,%]/g, '')
  ignore_trunks_edit = ignore_trunks.replace('  ', ' ')
  return ignore_trunks_edit
}

const get_list_ignore_trunks = (str_ignore_trunks: string) => {
  let list_ignore_trunks = str_ignore_trunks.split(' ')
  if (list_ignore_trunks[list_ignore_trunks.length - 1] === '') {
    list_ignore_trunks.splice(list_ignore_trunks.length - 1, 1)
  }

  return list_ignore_trunks
}

const get_ignore_trunks = (ignore_trunks: ITrunkModel[]) => {
  let list_ignore_trunks = []
  for (let i = 0; i < ignore_trunks.length; i++) {
    list_ignore_trunks.push(ignore_trunks[i].trunk)
  }
  if (list_ignore_trunks[list_ignore_trunks.length - 1] === '') {
    list_ignore_trunks.splice(list_ignore_trunks.length - 1, 1)
  }
  let str_ignore_trunks = list_ignore_trunks.join(' ')
  return str_ignore_trunks
}

const OtherSettings = () => {
  const intl = useIntl()
  const {appId} = useParams()
  const {data: app} = appsAPI.useFetchAppByIdQuery(appId!)
  const [otherSettingsState, updateOtherSettingsState] = useState(false)
  const [updateApp, {isLoading}] = appsAPI.useUpdateAppMutation()
  const [showApiKey, setShowApiKey] = useState(false)
  const [modeModal, setModeModal] = useState<'local' | 'cloud' | null>(null)

  const formik = useFormik<IOtherSettingsCreate>({
    initialValues: Object.assign(OtherSettingsInitValues, {
      crm_domain: app?.crm_domain,
      id_project: app?.crm === 'ROISTAT' ? app.settings.roistat.id_project : '',
      api_key: app?.crm === 'ROISTAT' ? app.settings.roistat.api_key : '',
      users_in_service: app?.crm === 'BITRIX24' ? app?.settings?.bitrix24.users_in_service : false,
      record_url: app?.pbx === 'ASTERISK' ? app?.settings?.asterisk.record_url : '',
      ignore_trunks: get_ignore_trunks(app?.settings.ignore_trunks ? app?.settings.ignore_trunks : []),
      mode_is_cloud: app?.crm === 'BITRIX24' ? (app?.settings.bitrix24.mode_is_cloud ? 'CLOUD' : 'LOCAL') : undefined,
      bitrix24_client_id:
        app?.crm === 'BITRIX24'
          ? app?.settings.bitrix24.credentials && app?.settings.bitrix24.credentials.client_id
          : undefined,
      bitrix24_client_secret:
        app?.crm === 'BITRIX24'
          ? app?.settings.bitrix24.credentials && app?.settings.bitrix24.credentials.client_secret
          : undefined,
    }),
    validationSchema: OtherSettingsEditShema,
    onSubmit: async (values) => {
      if (app?.crm === 'BITRIX24' && (values.mode_is_cloud === 'CLOUD') !== app.settings.bitrix24.mode_is_cloud) {
        setModeModal(values.mode_is_cloud === 'CLOUD' ? 'cloud' : 'local')
        return
      }
      await saveSettings(values)
    },
  })

  const saveSettings = async (values: IOtherSettingsCreate) => {
    let other_settings = {}
    if (app?.crm === 'AMOCRM' && app?.pbx === 'ASTERISK') {
      other_settings = {
        amocrm: {},
        asterisk: {
          record_url: values.record_url,
        },
        ignore_trunks: get_list_ignore_trunks(values.ignore_trunks),
      }
    } else if (app?.crm === 'BITRIX24' && app?.pbx === 'ASTERISK') {
      other_settings = {
        asterisk: {
          record_url: values.record_url,
        },
        bitrix24: {
          users_in_service: values.users_in_service,
          mode_is_cloud: values.mode_is_cloud === 'CLOUD',
          ...(values.mode_is_cloud === 'LOCAL' && {
            credentials: {
              client_id: values.bitrix24_client_id.trim(),
              client_secret: values.bitrix24_client_secret.trim(),
            },
          }),
        },
        ignore_trunks: get_list_ignore_trunks(values.ignore_trunks),
      }
    } else if (app?.crm === 'BITRIX24' && app?.pbx === 'YEASTAR') {
      other_settings = {
        bitrix24: {
          users_in_service: values.users_in_service,
          mode_is_cloud: values.mode_is_cloud === 'CLOUD',
          ...(values.mode_is_cloud === 'LOCAL' && {
            credentials: {
              client_id: values.bitrix24_client_id.trim(),
              client_secret: values.bitrix24_client_secret.trim(),
            },
          }),
        },
        ignore_trunks: get_list_ignore_trunks(values.ignore_trunks),
      }
    } else if (app?.crm === 'AMOCRM' && app?.pbx === 'YEASTAR') {
      other_settings = {
        amocrm: {},
        ignore_trunks: get_list_ignore_trunks(values.ignore_trunks),
      }
    } else if (app?.crm === 'ONEC' && app?.pbx === 'ASTERISK') {
      other_settings = {
        asterisk: {
          record_url: values.record_url,
        },
        ignore_trunks: get_list_ignore_trunks(values.ignore_trunks),
      }
    } else if (app?.crm === 'ROISTAT' && app?.pbx === 'ASTERISK') {
      if (!values.id_project?.trim()) {
        formik.setFieldError('id_project', `ID ${intl.formatMessage({id: 'FORMIK.ERROR.REQUIREDFIELD'})}`)
        return
      }
      if (!values.api_key?.trim()) {
        formik.setFieldError('api_key', `KEY ${intl.formatMessage({id: 'FORMIK.ERROR.REQUIREDFIELD'})}`)
        return
      }
      values.crm_domain = values.id_project?.trim()
      other_settings = {
        asterisk: {
          record_url: values.record_url,
        },
        roistat: {id_project: values.id_project, api_key: values.api_key},
        ignore_trunks: get_list_ignore_trunks(values.ignore_trunks),
      }
    }
    const data = {
      id: appId,
      crm_domain: values.crm_domain.replace('https:', '').replace('http:', '').replaceAll('/', ''),
      settings: other_settings,
    }
    await updateApp(data as IAppsModel)
      .unwrap()
      .then(() => {
        updateOtherSettingsState(false)
        formik.setFieldValue('crm_domain', data.crm_domain)
      })
      .catch((err) => {
        formik.setFieldError('crm_domain', err.data.detail)
      })
  }

  return (
    <>
      <Bitrix24ModeSwitchModal
        variant={modeModal}
        credentials={
          formik.values.mode_is_cloud === 'LOCAL'
            ? {
                client_id: (formik.values.bitrix24_client_id ?? '').trim(),
                client_secret: (formik.values.bitrix24_client_secret ?? '').trim(),
              }
            : undefined
        }
        onConfirm={() => {
          setModeModal(null)
          saveSettings(formik.values)
        }}
        onClose={() => setModeModal(null)}
      />
      {otherSettingsState === false && (
        <div className='card card-custom card-stretch shadow-sm mb-5'>
          <div className='card-header'>
            <div className='card-title m-0'>
              <h3 className='fw-bolder m-0'>{intl.formatMessage({id: 'APPS.SETTINGS.TITTLE'})}</h3>
            </div>
            <button className='btn btn-sm btn-primary align-self-center' onClick={() => updateOtherSettingsState(true)}>
              <KTSVG path='/media/icons/duotune/art/art005.svg' className='svg-icon-info me-1 align-self-center' />
              {intl.formatMessage({id: 'BUTTON.EDIT'})}
            </button>
          </div>
          <div className='card-body pt-5 pb-5'>
            {(app?.crm === 'BITRIX24' || app?.crm === 'AMOCRM') && <Domain />}
            {app?.crm === 'BITRIX24' && (
              <>
                <div className='separator separator-dashed my-5' />
                <div className='row mb-5'>
                  <label className='col-lg-4 fw-bolder text-gray-600 text-uppercase small'>Битрикс24 приложение</label>
                  <div className='col-lg-8'>
                    <Bitrix24AppCheck>
                      <div className='d-flex fw-bolder text-gray-800 me-2'>
                        {app?.settings.bitrix24.mode_is_cloud ? 'Приложение из маркета' : 'Локальное приложение'}
                      </div>
                    </Bitrix24AppCheck>
                  </div>
                </div>
                <div className='separator separator-dashed my-5' />
                <UseB24Users />
              </>
            )}
            {app?.crm === 'ONEC' && <AccessToken1C />}
            {app?.crm === 'ROISTAT' && <RoistatSettings />}
            {app?.pbx === 'ASTERISK' && (
              <>
                <div className='separator separator-dashed my-5' />
                <RecordURL />
              </>
            )}
            <div className='separator separator-dashed my-5'></div>
            <IgnoreLines />
          </div>
        </div>
      )}
      {otherSettingsState === true && (
        <form onSubmit={formik.handleSubmit} noValidate className='form'>
          <div className='card card-custom card-stretch shadow-sm mb-5'>
            <div className='card-header'>
              <div className='card-title m-0'>
                <h3 className='fw-bolder m-0'>{intl.formatMessage({id: 'APPS.SETTINGS.EDIT.TITTLE'})}</h3>
              </div>
            </div>
            <div className='card-body pt-5 pb-5'>
              {(app?.crm === 'BITRIX24' || app?.crm === 'AMOCRM') && (
                <div className='row mb-5'>
                  <label className='col-lg-4 col-form-label required fw-bolder text-gray-600'>
                    <span className='small text-uppercase'>{intl.formatMessage({id: 'APPS.CRMDOMAIN'})}</span>
                  </label>

                  <div className='col-lg-8'>
                    {formik.touched.crm_domain && formik.errors.crm_domain && (
                      <div className='fv-plugins-message-container text-center mb-2'>
                        <div className='fv-help-block fw-bolder text-danger'>{formik.errors.crm_domain}</div>
                      </div>
                    )}
                    <input
                      type='text'
                      className='form-control form-control-lg form-control-solid mb-3 mb-lg-0 text-gray-900'
                      placeholder={intl.formatMessage({id: 'APPS.CRMDOMAIN'})}
                      {...formik.getFieldProps('crm_domain')}
                    />
                  </div>
                </div>
              )}
              {app?.crm === 'BITRIX24' && (
                <>
                  <div className='row mb-5'>
                    <label className='col-lg-4 col-form-label fw-bolder text-gray-600'>
                      <span className='small text-uppercase'>Битрикс24 приложение</span>
                    </label>

                    <div className='col-lg-8 d-flex align-items-center'>
                      <select
                        name='pbx'
                        className='form-select form-select-lg form-select-solid'
                        {...formik.getFieldProps('mode_is_cloud')}
                      >
                        <option value='CLOUD'>Приложение из маркета</option>
                        <option value='LOCAL'>Локальное приложение</option>
                      </select>
                    </div>
                  </div>
                  {formik.values.mode_is_cloud === 'LOCAL' && (
                    <>
                      <div className='row mb-5'>
                        <label className='col-lg-4 col-form-label fw-bolder text-gray-600 required'>
                          <span className='small text-uppercase'>Код приложения (client_id)</span>
                        </label>
                        <div className='col-lg-8'>
                          {formik.touched.bitrix24_client_id && formik.errors.bitrix24_client_id && (
                            <div className='fv-plugins-message-container text-center mb-2'>
                              <div className='fv-help-block fw-bolder text-danger'>
                                {formik.errors.bitrix24_client_id}
                              </div>
                            </div>
                          )}
                          <input
                            type='text'
                            className='form-control form-control-lg form-control-solid mb-3 mb-lg-0 text-gray-900'
                            placeholder='Код приложения (client_id)'
                            {...formik.getFieldProps('bitrix24_client_id')}
                          />
                        </div>
                      </div>
                      <div className='row mb-5'>
                        <label className='col-lg-4 col-form-label fw-bolder text-gray-600 required'>
                          <span className='small text-uppercase'>Ключ приложения (client_secret)</span>
                        </label>
                        <div className='col-lg-8'>
                          {formik.touched.bitrix24_client_secret && formik.errors.bitrix24_client_secret && (
                            <div className='fv-plugins-message-container text-center mb-2'>
                              <div className='fv-help-block fw-bolder text-danger'>
                                {formik.errors.bitrix24_client_secret}
                              </div>
                            </div>
                          )}
                          <input
                            type='text'
                            className='form-control form-control-lg form-control-solid mb-3 mb-lg-0 text-gray-900'
                            placeholder='Ключ приложения (client_secret)'
                            {...formik.getFieldProps('bitrix24_client_secret')}
                          />
                        </div>
                      </div>
                    </>
                  )}
                </>
              )}
              {app?.crm === 'BITRIX24' && (
                <div className='row mb-5'>
                  <label className='col-lg-4 col-form-label fw-bolder text-gray-600'>
                    <span className='small text-uppercase'>
                      {intl.formatMessage({id: 'APPS.SETTINGS.BITRIX24.USERS'})}
                    </span>
                  </label>

                  <div className='col-lg-8 d-flex align-items-center'>
                    <div className='form-check form-switch form-check-custom form-check-solid me-10'>
                      <input
                        className='form-check-input h-20px w-30px align-self-center'
                        type='checkbox'
                        id='switchUseB24'
                        defaultChecked={app.settings.bitrix24.users_in_service === true ? true : false}
                        {...formik.getFieldProps('users_in_service')}
                      />
                    </div>
                  </div>
                </div>
              )}
              {app?.crm === 'ROISTAT' && (
                <>
                  <div className='row mb-5'>
                    <label className='col-lg-4 col-form-label required fw-bolder text-gray-600'>
                      <span className='small text-uppercase'>{intl.formatMessage({id: 'APPS.ROISTAT.PROJECT'})}</span>
                    </label>
                    <div className='col-lg-8'>
                      <input
                        type='text'
                        className='form-control form-control-lg form-control-solid mb-3 mb-lg-0 text-gray-900'
                        placeholder={intl.formatMessage({id: 'APPS.ROISTAT.PROJECT'})}
                        {...formik.getFieldProps('id_project')}
                      />
                      {formik.touched.id_project && formik.errors.id_project && (
                        <div className='fv-plugins-message-container'>
                          <div className='fv-help-block text-danger'>{formik.errors.id_project}</div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className='row mb-5'>
                    <label className='col-lg-4 col-form-label required fw-bolder text-gray-600'>
                      <span className='small text-uppercase'>{intl.formatMessage({id: 'APPS.ROISTAT.APIKEY'})}</span>
                    </label>

                    <div className='col-lg-8'>
                      <div className='input-group input-group-solid'>
                        <input
                          type={showApiKey ? 'text' : 'password'}
                          className='form-control form-control-lg form-control-solid text-gray-900'
                          placeholder={intl.formatMessage({id: 'APPS.ROISTAT.APIKEY'})}
                          autoComplete='off'
                          {...formik.getFieldProps('api_key')}
                        />
                        <span
                          className='input-group-text border-0'
                          style={{cursor: 'pointer'}}
                          onClick={() => setShowApiKey(!showApiKey)}
                        >
                          <i
                            className={clsx('bi text-gray-800 fs-3', {
                              'bi-eye-fill': !showApiKey,
                              'bi-eye-slash-fill': showApiKey,
                            })}
                          ></i>
                        </span>
                      </div>
                      {formik.touched.api_key && formik.errors.api_key && (
                        <div className='fv-plugins-message-container'>
                          <div className='fv-help-block text-danger'>{formik.errors.api_key}</div>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
              {app?.pbx === 'ASTERISK' && (
                <div className='row mb-5'>
                  <label className='col-lg-4 col-form-label fw-bolder text-gray-600'>
                    <span className='small text-uppercase'>{intl.formatMessage({id: 'APPS.SETTINGS.RECORDURL'})} </span>
                  </label>
                  <div className='col-lg-8'>
                    <input
                      type='text'
                      className='form-control form-control-lg form-control-solid mb-3 mb-lg-0 text-gray-900'
                      placeholder='http://123.123.123.123:38080/ or https://example.com:38080/'
                      {...formik.getFieldProps('record_url')}
                    />
                  </div>
                </div>
              )}
              <div className='row mb-5'>
                <label className='col-lg-4 col-form-label fw-bolder text-gray-600'>
                  <span className='small text-uppercase'>{intl.formatMessage({id: 'APPS.SETTINGS.IGNORE'})}</span>
                </label>

                <div className='col-lg-8'>
                  <input
                    type='text'
                    className='form-control form-control-lg form-control-solid mb-3 mb-lg-0 text-gray-900'
                    placeholder='+375291234567 +375441111111'
                    onInput={(e: any) => {
                      e.target.value = check_ignore_trunks(e.target.value)
                    }}
                    {...formik.getFieldProps('ignore_trunks')}
                  />
                </div>
              </div>
              <div className='card-footer d-flex justify-content-end py-2 px-1' style={{border: 'none'}}>
                <button type='submit' className='btn btn-sm btn-primary me-3' disabled={isLoading}>
                  {!isLoading && intl.formatMessage({id: 'BUTTON.SAVE'})}
                  {isLoading && (
                    <span className='indicator-progress' style={{display: 'block'}}>
                      {intl.formatMessage({id: 'WAIT'})}
                      <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                    </span>
                  )}
                </button>
                <button
                  className='btn btn-sm btn-light-primary align-self-center'
                  onClick={() => {
                    updateOtherSettingsState(false)
                    formik.resetForm()
                  }}
                >
                  {intl.formatMessage({id: 'BUTTON.CANCEL'})}
                </button>
              </div>
            </div>
          </div>
        </form>
      )}
    </>
  )
}

export {OtherSettings}
